// src/lib/posthog/signup-events.ts
//
// Pure decision rules for the sign-up form's lifecycle events:
// `signup_start`, `signup_email_entered`, `signup_submit`, `signup_failed`
// and `signup_success`. No `window`, no react-hook-form import, no
// `capture` call: `sign-up-form.tsx` owns the `useEffect`/`useRef` wiring
// and the form state, and calls these functions with plain values, so the
// decisions stay testable with literal tables — this repo's Vitest config
// runs with `environment: "node"` (no jsdom), so nothing inside a `.tsx` is
// reachable by a test.

/**
 * The closed property shape for every signup lifecycle event
 * (`events.ts`'s `SIGNUP_LIFECYCLE_PROPERTIES`): `signup_method` (always
 * `"email"`, the only method today) and `country`. `input` may
 * carry other fields (a caller composing this from a react-hook-form
 * `values` object that also has `email`, `phone`, `password`, etc.) — only
 * `country` is ever read out of it, so an email, a phone or a
 * password-strength score can never reach a captured property this way.
 */
export interface SignupPropertiesInput {
  country?: string | null;
  [key: string]: unknown;
}

export function signupProperties(
  input: SignupPropertiesInput,
): { signup_method: "email"; country: string } {
  const country = typeof input.country === "string" ? input.country : "";
  return { signup_method: "email", country };
}

export type SignupFailureStage = "validation" | "request";

/**
 * The codes this module produces itself, for the cases Better Auth does not
 * name. A recognised Better Auth `code` is passed through unchanged instead,
 * so `reason_code` is deliberately typed as `string` at the call sites
 * rather than as a closed union.
 */
export type SignupOwnReasonCode =
  | "VALIDATION_FAILED"
  | "RATE_LIMITED"
  | "PASSWORD_POLICY"
  | "UNEXPECTED";

/**
 * The `stage: "validation"` case: react-hook-form's `onInvalid` handler
 * (zod rejected the form). The `reason_code` is a single stable value
 * regardless of which field(s) failed — the field name and the zod message
 * are never read, so a value a merchant typed can never leak through here.
 */
export const SIGNUP_VALIDATION_FAILURE: {
  reason_code: "VALIDATION_FAILED";
  stage: "validation";
} = { reason_code: "VALIDATION_FAILED", stage: "validation" };

// `login_failed.reason_code` and `signup_failed.reason_code` are Better Auth's
// error `code` string (e.g. `INVALID_EMAIL_OR_PASSWORD`,
// `USER_ALREADY_EXISTS`), never the message. So the code is passed THROUGH,
// not renamed: a local mapping would have to be extended for every new Better
// Auth code, and would make `signup_failed` and `login_failed` disagree
// about the same failure, which breaks any funnel that compares them.
//
// The shape guard is the safety net that lets a pass-through be safe: only a
// SCREAMING_SNAKE token of sane length is accepted, so if `code` ever carried
// something free-text it lands in the bounded fallbacks below instead.
const BETTER_AUTH_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;

function stringField(value: object, key: string): string | undefined {
  const field = (value as Record<string, unknown>)[key];
  return typeof field === "string" ? field : undefined;
}

function numberField(value: object, key: string): number | undefined {
  const field = (value as Record<string, unknown>)[key];
  return typeof field === "number" ? field : undefined;
}

/**
 * The shape-guarded Better Auth error -> `reason_code` mapper, shared by
 * `signup_failed` and `login_failed` so the two events
 * cannot drift into two different vocabularies for the same underlying
 * failure. `error` is `unknown` on purpose: a thrown value is never
 * guaranteed to be Better Auth's error shape. A recognised Better Auth
 * `code` wins; failing that, `status` 429 (Better Auth's database rate
 * limiter) or 400 (this repo's own `passwordPolicyError` in auth.ts, which
 * throws `APIError("BAD_REQUEST", { message })` with no `code`) map to their
 * own stable codes; anything else — including a non-object throw — is
 * `"UNEXPECTED"`. `error.message` is never read: a free-text reason is
 * exactly what the rule above rules out.
 */
export function betterAuthReasonCode(error: unknown): string {
  if (typeof error !== "object" || error === null) {
    return "UNEXPECTED";
  }
  const code = stringField(error, "code");
  if (code && BETTER_AUTH_CODE.test(code)) {
    return code;
  }
  // No usable `code`. These three are ours, in Better Auth's own casing so a
  // funnel reads one consistent vocabulary: 429 is Better Auth's rate limiter,
  // 400 is this repo's `passwordPolicyError` (an `APIError("BAD_REQUEST")`
  // carrying only a message), and anything else is unclassified.
  const status = numberField(error, "status");
  if (status === 429) return "RATE_LIMITED";
  if (status === 400) return "PASSWORD_POLICY";
  return "UNEXPECTED";
}

/**
 * The `stage: "request"` mapper, for both the `{ error }` branch of
 * `authClient.signUp.email` and the outer try/catch's thrown value.
 */
export function signupRequestFailureReason(
  error: unknown,
): { reason_code: string; stage: "request" } {
  return { reason_code: betterAuthReasonCode(error), stage: "request" };
}
